import { StateCreator } from 'zustand'
import { WidgetStateSlice } from './widgetStateSlice'
import { useGlobalStore } from './globalStore';


// STATE
const gracePeriodMs = 5_000
const openDelayMs = 1_000
interface ProactiveSlice {
    recheckDelayLast: number
    proactiveOnTick: () => void
    proactives: Array<Proactive> // TODO: consider using Map to make duplicate ids impossible
    currentProactive: Proactive | null
    proactiveSetTime: number
    proactiveGraceperiodStart: number
    proactiveShouldOpen: boolean
    proactivesSet: (proactives: Array<ProactiveRaw>) => void
    proactiveDismiss: () => void
    proactiveInGracePeriod: () => boolean
    chatTopLinks: Array<ChatTopLink>
    // chatTopLinksLoad: () => void
}
const createProactiveSlice: StateCreator<
    ProactiveSlice & WidgetStateSlice,
    [],
    [],
    ProactiveSlice
> = (set, get) => (
    {
        proactives: [],
        currentProactive: null,
        proactiveSetTime: 0,
        proactiveGraceperiodStart: Date.now(),
        proactiveShouldOpen: false,
        recheckDelayLast: 0,
        chatTopLinks: [],
        proactiveOnTick: () => {
            const state = get();
            set((state) => ({ ...state, proactiveShouldOpen: !!get().currentProactive && !get().proactiveInGracePeriod() && !!get().proactiveSetTime && get().proactiveSetTime + openDelayMs <= Date.now() }));
            if (state.connection.status === "Up" || state.open !== "closed" || get().proactiveInGracePeriod()) {
                return;
            }
            const proactives = state.proactives
            const next = proactives
                .filter(proactiveDepFilter(get().widgetConfName))
                .filter((p) => p.activeCurrently)
                .find((p) => {
                    const now = Date.now();
                    if (p.dismissedDate === 0) {
                        const timeout = p.timeoutInitialMin;
                        return (timeout * 60 * 1000) < (now - p.registeredDate);
                    } else {
                        const timeout = p.timeoutDismissedMin;
                        return (timeout * 60 * 1000) < (now - p.dismissedDate);
                    }
                });
            if (!next) {
                return null;
            }
            useGlobalStore.setState({ currentProactive: next, open: "proactive", proactiveSetTime: Date.now() });
        },
        proactivesSet: (proactives: Array<ProactiveRaw>) => {
            console.log("proactivesSet", proactives);
            // register new proactives and use already registered and dismissed times for matching ids.
            const newValid = proactives
                .map(mergeProactives(get().proactives))
            console.log("newValid", newValid);
            // set activeCurrently to true for proactives that are in the newValid array and false for the rest.
            const previous = get().proactives
                .filter((prev) => !newValid.some((p) => p.id === prev.id))
                .map((p) => ({ ...p, activeCurrently: false }));

            console.log("previous    - ", [...previous, ...newValid].map((p) => ({ id: p.id, active: p.activeCurrently })));
            console.log("allPrevious - ", get().proactives.map((p) => ({ id: p.id, active: p.activeCurrently })));

            set((state) => ({ ...state, proactives: [...previous, ...newValid] }));
        },
        proactiveDismiss: () => {
            const id = get().currentProactive?.id;
            if (!id) {
                throw new Error("No current proactive to dismiss");
            }
            const proactives = get().proactives
            const p = proactives.find((p) => p.id === id);
            const newOpenState = get().open === "proactive" ? "closed" : get().open;
            if (!p) {
                throw new Error(`Proactive with id ${id} not found`);
            }
            p.dismissedDate = Date.now();
            set((state) => ({
                ...state, open: newOpenState,
                proactiveShouldOpen: false,
                proactiveSetTime: 0,
                proactives,
            }));
            setTimeout(() => {
                set((state) => ({
                    ...state,
                    currentProactive: null,
                    proactiveGraceperiodStart: Date.now()
                }));
            }, openDelayMs);
        },
        proactiveInGracePeriod: () => {
            return get().proactiveGraceperiodStart + gracePeriodMs > Date.now();
        },
        chatTopLinksLoad: () => {
            const newLinks = loadChatTopLinks();
            set((state) => ({ ...state, chatTopLinks: newLinks }));
        },
    }
)

// PROACTIVES
// function loadProactives(loaded: Array<Proactive>): Array<Proactive> {
//     if (!window.dpj || !window.dpj.widget || !window.dpj.widget.proactives) {
//         return [];
//     }

//     const fromWindow = window.dpj.widget.proactives as Array<ProactiveRaw>;

//     // handle new proactives
//     const newValid = fromWindow
//         .filter((fw) => {
//             return (!loaded.some((p) => p.id === fw.id) && checkRaw(fw));
//         }).map(fromRaw);

//     const currValid = fromWindow
//         .filter((fw) => {
//             return (loaded.some((p) => p.id === fw.id) && checkRaw(fw));
//         }).map(curr => {
//             const p = loaded.find((p) => p.id === curr.id);
//             if (!p) {
//                 throw new Error(`Proactive with id ${curr.id} not found`);
//             }
//             return { ...p, ...curr }
//         });

//     return newValid.concat(currValid);
// }

function proactiveDepFilter(widgetConfName: string | null): (p: Proactive) => boolean {
    return (p) => {
        return !!widgetConfName && (p.dependsOnConfigs.length === 0 || p.dependsOnConfigs.some((c) => widgetConfName === c))
    }
}

function mergeProactives(current: Array<Proactive>): (pNew: ProactiveRaw) => Proactive {
    return (pNew) => {
        const dependsOnConfigs = pNew.dependsOnConfigs || [];
        const existing = current.find((p) => p.id === pNew.id);
        if (existing) {
            const merged = { ...pNew, activeCurrently: true, registeredDate: existing.registeredDate, dismissedDate: existing.dismissedDate, dependsOnConfigs };
            return merged;
        }
        return { ...pNew, activeCurrently: true, registeredDate: Date.now(), dismissedDate: 0, dependsOnConfigs };
    }
}

const checkRaw = (raw: unknown): raw is ProactiveRaw => {
    if (
        typeof raw !== "object" || raw === null ||
        !("type" in raw) ||
        !("id" in raw) ||
        !("timeoutInitialMin" in raw) ||
        !("timeoutDismissedMin" in raw)
    ) {
        return false;
    }
    if (raw.type === "chat") {
        return "text" in raw &&
            "ctaText" in raw &&
            "avatarUrl" in raw &&
            "avatarAlt" in raw &&
            "displayName" in raw &&
            "inChatMessage" in raw;
    } else if (raw.type === "link") {
        return "title" in raw &&
            "text" in raw &&
            "ctaUrl" in raw;
    }
    return false;
}


type Base = {
    id: string;
    timeoutInitialMin: number;
    timeoutDismissedMin: number | 0; // 0 means never show again;
    dependsOnConfigs?: string[];
    activeCurrently?: boolean;
};
type Registered = {
    registeredDate: number;
    dismissedDate: number | 0; // 0 means not yet dismissed,
    activeCurrently: boolean;
    dependsOnConfigs: string[];
};

type Default = Base &
    Registered & {
        type: "default";
    };

type Chat = Base & {
    type: "chat";
    text: string;
    ctaText: string;
    inChatMessage: string;
    avatarUrl: string;
    displayName: string;
    avatarAlt: string;
};

type Link = Base & {
    type: "link";
    title: string;
    text: string;
    ctaText: string;
    ctaUrl: string;
};

type ProactiveRaw = Chat | Link | Default;

type Proactive = ProactiveRaw & Registered;


// TYPES CHAT TOP LINKS
type ChatTopLink = { label: string, href: string }
function isChatTopLink(data: unknown): data is ChatTopLink {
    if (
        typeof data !== "object" ||
        data === null ||
        !("label" in data) ||
        typeof data.label !== "string" ||
        !("href" in data) ||
        typeof data.href !== "string"
    ) {
        return false;
    }
    return true;
}

const loadChatTopLinks = (): Array<ChatTopLink> => {
    let chatTopLinks = window.dpj?.widget?.chat_top_links || [];
    chatTopLinks = chatTopLinks.filter(isChatTopLink);
    return chatTopLinks as Array<ChatTopLink>;
}


export type { ProactiveSlice, Proactive, ProactiveRaw, ChatTopLink }
export { createProactiveSlice, checkRaw, isChatTopLink }
